import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';

import { ComponentRegistry } from 'core/registry';

import { Status, StatusIndicator } from 'apps/rfq/rfqPage/views/openRFQ.styled';
import { Option, OptionWrapper } from 'libs/forms/styles';

import rfqStates from 'apps/rfq/rfqStates';
import type { RFQState } from 'apps/rfq/rfqStates';
import orderStatuses from 'apps/order/orderStatuses';
import type { OrderStatus } from 'apps/order/orderStatuses';

const states: any = {
  rfq: rfqStates,
  order: orderStatuses,
};

const StatusDisplayWrapper = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
});

type Types = 'rfq' | 'order';
type StatusKey = RFQState | OrderStatus;

type StatusDisplayProps = {
  noLabel?: boolean;
  status: StatusKey;
  type: Types;
};

type RFQStatusDisplayProps = {
  noLabel?: boolean;
  status: StatusKey;
};

export const StatusDisplay = observer(
  ({ noLabel, status, type }: StatusDisplayProps) => {
    const statusText = states[type][status]?.title || 'Unknown';
    const statusColor = states[type][status]?.color || 'wfGrey';
    return (
      <StatusDisplayWrapper>
        {!noLabel && <Status>Status:</Status>}
        {statusText}
        <StatusIndicator color={statusColor} />
      </StatusDisplayWrapper>
    );
  },
);

export const RFQStatusDisplay = ({
  noLabel,
  status,
}: RFQStatusDisplayProps) => {
  return <StatusDisplay noLabel={noLabel} status={status} type='rfq' />;
};

type StatusOptionProps = {
  app: { status: string; isExpandable: boolean };
  scrollRef: any;
  highlighted: boolean;
  highlightedToExpand: boolean;
  singleItem: boolean;
  isSelectableForEntireCategory: boolean;
  expanded: boolean | null;
  onClick: () => {};
  onMouseOver: () => {};
  type?: Types;
};

export const StatusOption = observer(
  ({
    app,
    highlighted,
    highlightedToExpand,
    onMouseOver,
    onClick,
    scrollRef,
    singleItem = false,
    expanded = null,
    isSelectableForEntireCategory,
    type,
  }: StatusOptionProps) => {
    return (
      <OptionWrapper
        ref={scrollRef}
        highlighted={highlighted}
        highlightedToExpand={highlightedToExpand && !singleItem}
        onMouseOver={onMouseOver}
        onClick={onClick}
      >
        <Option
          highlighted={highlighted}
          indentLevel={2}
          isExpandable={app.isExpandable}
          expanded={!!expanded}
          isSelectableForEntireCategory={isSelectableForEntireCategory}
          isAutoExpandedCategory={singleItem}
        >
          <StatusDisplay status={app.status as StatusKey} noLabel type={type} />
        </Option>
      </OptionWrapper>
    );
  },
);

export const RFQStatusOption = observer(({ ...props }: StatusOptionProps) => {
  return <StatusOption {...props} type='rfq' />;
});

type RFQStatusSelectionProps = {
  selected: { status: RFQState };
};

const RFQStatusSelection = observer(({ selected }: RFQStatusSelectionProps) => (
  <StatusDisplay status={selected.status} noLabel type='rfq' />
));

export const OrderStatusOption = observer(({ ...props }: StatusOptionProps) => {
  return <StatusOption {...props} type='order' />;
});

type OrderStatusSelectionProps = {
  selected: { status: OrderStatus };
};

const OrderStatusSelection = observer(
  ({ selected }: OrderStatusSelectionProps) => (
    <StatusDisplay status={selected.status} noLabel type='order' />
  ),
);

ComponentRegistry.add('rfq.StatusSelection', RFQStatusSelection);
ComponentRegistry.add('rfq.StatusOption', RFQStatusOption);
ComponentRegistry.add('order.StatusSelection', OrderStatusSelection);
ComponentRegistry.add('order.StatusOption', OrderStatusOption);
